import React, { useState, useRef, useEffect } from 'react';
import { Settings, HelpCircle, LogOut, Mail, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export function UserMenu({ user, onSettings, onHelp, onLogout }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayName = user.username || user.name || user.email?.split('@')[0] || 'Usuario';

  const Avatar = ({ size }) => user.picture ? (
    <img
      src={user.picture}
      alt={displayName}
      className={`${size} rounded-full object-cover border-2 border-blue-500 shadow-sm`}
      referrerPolicy="no-referrer"
    />
  ) : (
    <div className={`${size} rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-xs border-2 border-blue-500 shadow-sm`}>
      {(user.username || user.email || 'U')[0].toUpperCase()}
    </div>
  );

  const item = (onClick, testId, icon, label, extra = '') => (
    <button
      onClick={() => { setOpen(false); onClick(); }}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors ${extra}`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="user-menu-btn"
        className="flex items-center gap-1 p-0.5 rounded-full hover:bg-gray-100 transition-colors active:scale-95"
        title={displayName}
      >
        <Avatar size="w-7 h-7 sm:w-8 sm:h-8" />
        <ChevronDown size={12} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="user-menu-dropdown">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
            <Avatar size="w-10 h-10" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-800 truncate">{displayName}</p>
              <p className="text-xs text-gray-500 truncate flex items-center gap-1" data-testid="user-menu-email">
                <Mail size={11} className="shrink-0" />
                {user.email || 'N/A'}
              </p>
            </div>
          </div>
          <div className="py-1">
            {item(onSettings, 'user-menu-settings', <Settings size={16} className="text-gray-400" />, language === 'es' ? 'Ajustes' : 'Settings')}
            {item(onHelp, 'user-menu-help', <HelpCircle size={16} className="text-gray-400" />, language === 'es' ? 'Ayuda' : 'Help')}
          </div>
          <div className="border-t border-gray-100 py-1">
            {item(onLogout, 'user-menu-logout', <LogOut size={16} />, language === 'es' ? 'Salir' : 'Logout', '!text-red-500 hover:!bg-red-50')}
          </div>
        </div>
      )}
    </div>
  );
}
